/**
 * IndexedDB-backed asset storage for user-uploaded images and files.
 * Assets are stored as Blobs with metadata. Blob URLs are cached for reuse.
 */

import { createIdbHandle, withStore, promisifyRequest } from './idb.js';

const DB_NAME = 'pb-assets';
const DB_VERSION = 1;
const STORE_NAME = 'blobs';

// Shared self-healing connection handle (resolves globalThis.indexedDB lazily,
// so a test can install a fake factory before the first asset op).
const assetIdb = createIdbHandle({
  name: DB_NAME,
  version: DB_VERSION,
  upgrade: (db) => {
    if (!db.objectStoreNames.contains(STORE_NAME)) {
      db.createObjectStore(STORE_NAME, { keyPath: 'id' });
    }
  }
});

function readRecord(record) {
  if (!record) return null;
  return {
    blob: record.blob,
    filename: record.filename,
    mime: record.mime,
    source: record.source,
    author: record.author,
    authorUrl: record.authorUrl,
    hash: record.hash
  };
}

async function blobSha256(blob) {
  const cryptoApi = globalThis.crypto;
  if (!blob || !cryptoApi?.subtle || typeof blob.arrayBuffer !== 'function') return '';
  const data = await blob.arrayBuffer();
  const digest = await cryptoApi.subtle.digest('SHA-256', data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export class AssetStore {
  constructor() {
    this._blobUrlCache = new Map();
    // Collab Phase 4: optional post-commit listener installed by
    // collab-assets.js while a shared project is open (upload mirror).
    // Never installed for solo projects; errors in it must not break stores.
    this.onStored = null;
  }

  _emitStored(payload) {
    if (typeof this.onStored !== 'function') return;
    try {
      this.onStored(payload);
    } catch (err) {
      console.warn('[AssetStore] onStored listener failed:', err);
    }
  }

  /**
   * Store a blob under a freshly-generated UUID. Resolves with the new id
   * AFTER the write commits (durability).
   * @param {{notify?:boolean}} [options] — notify:false suppresses onStored
   */
  async store(file, meta, { notify = true } = {}) {
    const hash = await blobSha256(file).catch(() => '');
    if (hash) {
      const records = await withStore(assetIdb, STORE_NAME, 'readonly', (objStore) =>
        promisifyRequest(objStore.getAll())
      );
      const existing = (records || []).find((record) => record?.hash === hash);
      if (existing?.id) {
        // Dedupe reuse still notifies: a locally-reused asset may have never
        // been uploaded to the (newly shared) project this store targets.
        if (notify) {
          this._emitStored({
            id: existing.id,
            filename: existing.filename,
            mime: existing.mime,
            source: existing.source,
            author: existing.author,
            authorUrl: existing.authorUrl,
            hash: existing.hash
          });
        }
        return existing.id;
      }
    }
    const id = crypto.randomUUID();
    return this.storeWithId(
      id,
      file,
      {
        filename: file.name || 'untitled',
        mime: file.type || 'application/octet-stream',
        source: meta?.source,
        author: meta?.author,
        authorUrl: meta?.authorUrl,
        hash
      },
      { notify }
    );
  }

  /**
   * Store a blob under a caller-supplied id (used when restoring from a ZIP,
   * where asset ids must be preserved). Resolves with the id after commit.
   * @param {string} id
   * @param {Blob} blob
   * @param {{filename?:string, mime?:string, source?:string, author?:string, authorUrl?:string, hash?:string}} [meta]
   * @param {{notify?:boolean}} [options] — notify:false for restore paths and
   *   remote-fetch writes (they must never echo back into the upload queue)
   */
  async storeWithId(id, blob, meta = {}, { notify = true } = {}) {
    const record = {
      id,
      blob,
      filename: meta.filename || (blob && blob.name) || 'untitled',
      mime: meta.mime || (blob && blob.type) || 'application/octet-stream',
      created: Date.now()
    };
    // Persist attribution metadata for legal/source traceability
    if (meta.source) record.source = meta.source;
    if (meta.author) record.author = meta.author;
    if (meta.authorUrl) record.authorUrl = meta.authorUrl;
    if (meta.hash) record.hash = meta.hash;
    await withStore(assetIdb, STORE_NAME, 'readwrite', (objStore) => {
      objStore.put(record);
    });
    if (notify) {
      this._emitStored({
        id,
        filename: record.filename,
        mime: record.mime,
        source: record.source,
        author: record.author,
        authorUrl: record.authorUrl,
        hash: record.hash
      });
    }
    return id;
  }

  async get(id) {
    const record = await withStore(assetIdb, STORE_NAME, 'readonly', (objStore) =>
      promisifyRequest(objStore.get(id))
    );
    return readRecord(record);
  }

  async getBlobUrl(id) {
    if (this._blobUrlCache.has(id)) return this._blobUrlCache.get(id);
    const record = await this.get(id);
    if (!record) return null;
    const url = URL.createObjectURL(record.blob);
    this._blobUrlCache.set(id, url);
    return url;
  }

  async delete(id) {
    if (this._blobUrlCache.has(id)) {
      URL.revokeObjectURL(this._blobUrlCache.get(id));
      this._blobUrlCache.delete(id);
    }
    await withStore(assetIdb, STORE_NAME, 'readwrite', (objStore) => {
      objStore.delete(id);
    });
  }

  async exportAll() {
    const records = await withStore(assetIdb, STORE_NAME, 'readonly', (objStore) =>
      promisifyRequest(objStore.getAll())
    );
    const map = new Map();
    for (const record of records || []) {
      map.set(record.id, readRecord(record));
    }
    return map;
  }

  async gc(referencedIds) {
    const refSet = referencedIds instanceof Set ? referencedIds : new Set(referencedIds);
    const allKeys = await withStore(assetIdb, STORE_NAME, 'readonly', (objStore) =>
      promisifyRequest(objStore.getAllKeys())
    );

    const toDelete = (allKeys || []).filter((id) => !refSet.has(id));
    for (const id of toDelete) {
      await this.delete(id);
    }
    return toDelete.length;
  }

  revokeAll() {
    for (const url of this._blobUrlCache.values()) {
      URL.revokeObjectURL(url);
    }
    this._blobUrlCache.clear();
  }
}
